import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { ArrowLeft, Save, User, Phone, MapPin, Calendar, IndianRupee } from 'lucide-react';
import { Timestamp } from 'firebase/firestore';
import { workerService } from '../../services/firebase';
import { Worker } from '../../types';

interface WorkerFormData {
  fullName: string;
  phoneNumber: string;
  address: string;
  gender: 'male' | 'female' | 'other';
  onboardingDate: string;
  availabilityStatus: 'active' | 'inactive' | 'on_leave';
  salary?: number;
}

interface ValidationErrors {
  fullName?: string;
  phoneNumber?: string;
  address?: string;
  gender?: string;
  onboardingDate?: string;
  salary?: string;
}

const WorkerForm: React.FC = () => {
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const isEditing = Boolean(id);

  const [formData, setFormData] = useState<WorkerFormData>({
    fullName: '',
    phoneNumber: '',
    address: '',
    gender: 'male',
    onboardingDate: new Date().toISOString().split('T')[0],
    availabilityStatus: 'active',
    salary: undefined
  });

  const [errors, setErrors] = useState<ValidationErrors>({});
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (isEditing && id) {
      loadWorker();
    }
  }, [isEditing, id]);

  const loadWorker = async () => {
    try {
      setLoading(true);
      setError(null);
      const worker = await workerService.getWorkerById(id!);
      
      if (worker) {
        setFormData({
          fullName: worker.fullName,
          phoneNumber: worker.phoneNumber,
          address: worker.address,
          gender: worker.gender,
          onboardingDate: worker.onboardingDate.toDate().toISOString().split('T')[0],
          availabilityStatus: worker.availabilityStatus,
          salary: worker.salary
        });
      } else {
        setError('Worker not found');
      }
    } catch (err) {
      setError('Failed to load worker. Please try again.');
      console.error('Error loading worker:', err);
    } finally {
      setLoading(false);
    }
  };

  const validateForm = (): boolean => {
    const newErrors: ValidationErrors = {};

    // Full Name validation
    if (!formData.fullName.trim()) {
      newErrors.fullName = 'Full name is required';
    } else if (formData.fullName.trim().length < 2) {
      newErrors.fullName = 'Full name must be at least 2 characters';
    } else if (!/^[a-zA-Z\s]+$/.test(formData.fullName.trim())) {
      newErrors.fullName = 'Full name can only contain letters and spaces';
    }

    // Phone Number validation
    if (!formData.phoneNumber.trim()) {
      newErrors.phoneNumber = 'Phone number is required';
    } else if (!/^[6-9]\d{9}$/.test(formData.phoneNumber.trim())) {
      newErrors.phoneNumber = 'Please enter a valid 10-digit Indian mobile number';
    }

    // Address validation
    if (!formData.address.trim()) {
      newErrors.address = 'Address is required';
    } else if (formData.address.trim().length < 10) {
      newErrors.address = 'Address must be at least 10 characters';
    }

    // Gender validation
    if (!formData.gender) {
      newErrors.gender = 'Please select a gender';
    }

    // Onboarding Date validation
    if (!formData.onboardingDate) {
      newErrors.onboardingDate = 'Onboarding date is required';
    } else {
      const selectedDate = new Date(formData.onboardingDate);
      const today = new Date();
      if (selectedDate > today) {
        newErrors.onboardingDate = 'Onboarding date cannot be in the future';
      }
    }

    // Salary validation
    if (formData.salary !== undefined && formData.salary !== null) {
      if (formData.salary < 0) {
        newErrors.salary = 'Salary must be a positive number';
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleInputChange = (field: keyof WorkerFormData, value: any) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    
    // Clear error for this field when user starts typing
    if (errors[field as keyof ValidationErrors]) {
      setErrors(prev => ({ ...prev, [field]: undefined }));
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }

    try {
      setSaving(true);
      setError(null);

      const workerData = {
        fullName: formData.fullName.trim(),
        phoneNumber: formData.phoneNumber.trim(),
        address: formData.address.trim(),
        gender: formData.gender,
        onboardingDate: Timestamp.fromDate(new Date(formData.onboardingDate)),
        availabilityStatus: formData.availabilityStatus,
        salary: formData.salary || undefined
      };

      if (isEditing && id) {
        await workerService.updateWorker(id, workerData);
      } else {
        await workerService.addWorker({
          ...workerData,
          isActive: true
        });
      }

      navigate('/workers');
    } catch (err: any) {
      if (err.message.includes('Phone number already exists')) {
        setErrors({ phoneNumber: 'Phone number already exists for an active worker' });
      } else {
        setError('Failed to save worker. Please try again.');
      }
      console.error('Error saving worker:', err);
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-green-600"></div>
      </div>
    );
  }

  return (
    <div className="max-w-2xl mx-auto">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center">
          <button
            onClick={() => navigate('/workers')}
            className="mr-4 text-gray-400 hover:text-gray-600"
          >
            <ArrowLeft className="h-5 w-5" />
          </button>
          <div>
            <h1 className="text-2xl font-bold text-gray-900">
              {isEditing ? 'Edit Worker' : 'Add New Worker'}
            </h1>
            <p className="mt-1 text-sm text-gray-600">
              {isEditing ? 'Update worker information' : 'Add a new worker to your team'}
            </p>
          </div>
        </div>
      </div>

      {/* Error Message */}
      {error && (
        <div className="mb-6 bg-red-50 border border-red-200 rounded-md p-4">
          <div className="flex">
            <div className="ml-3">
              <h3 className="text-sm font-medium text-red-800">Error</h3>
              <div className="mt-2 text-sm text-red-700">
                <p>{error}</p>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Form */}
      <form onSubmit={handleSubmit} className="bg-white shadow rounded-lg p-6 space-y-6">
        {/* Full Name */}
        <div>
          <label htmlFor="fullName" className="block text-sm font-medium text-gray-700 mb-2">
            <User className="inline h-4 w-4 mr-1" />
            Full Name *
          </label>
          <input
            type="text"
            id="fullName"
            value={formData.fullName}
            onChange={(e) => handleInputChange('fullName', e.target.value)}
            className={`block w-full px-3 py-2 border rounded-md shadow-sm focus:outline-none focus:ring-green-500 focus:border-green-500 sm:text-sm ${
              errors.fullName ? 'border-red-300' : 'border-gray-300'
            }`}
            placeholder="Enter full name"
          />
          {errors.fullName && (
            <p className="mt-1 text-sm text-red-600">{errors.fullName}</p>
          )}
        </div>

        {/* Phone Number */}
        <div>
          <label htmlFor="phoneNumber" className="block text-sm font-medium text-gray-700 mb-2">
            <Phone className="inline h-4 w-4 mr-1" />
            Phone Number *
          </label>
          <input
            type="tel"
            id="phoneNumber"
            value={formData.phoneNumber}
            onChange={(e) => handleInputChange('phoneNumber', e.target.value)}
            className={`block w-full px-3 py-2 border rounded-md shadow-sm focus:outline-none focus:ring-green-500 focus:border-green-500 sm:text-sm ${
              errors.phoneNumber ? 'border-red-300' : 'border-gray-300'
            }`}
            placeholder="Enter 10-digit mobile number"
            maxLength={10}
          />
          {errors.phoneNumber && (
            <p className="mt-1 text-sm text-red-600">{errors.phoneNumber}</p>
          )}
        </div>

        {/* Address */}
        <div>
          <label htmlFor="address" className="block text-sm font-medium text-gray-700 mb-2">
            <MapPin className="inline h-4 w-4 mr-1" />
            Address *
          </label>
          <textarea
            id="address"
            value={formData.address}
            onChange={(e) => handleInputChange('address', e.target.value)}
            rows={3}
            className={`block w-full px-3 py-2 border rounded-md shadow-sm focus:outline-none focus:ring-green-500 focus:border-green-500 sm:text-sm ${
              errors.address ? 'border-red-300' : 'border-gray-300'
            }`}
            placeholder="Enter complete address"
          />
          {errors.address && (
            <p className="mt-1 text-sm text-red-600">{errors.address}</p>
          )}
        </div>

        {/* Gender and Onboarding Date */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div>
            <label htmlFor="gender" className="block text-sm font-medium text-gray-700 mb-2">
              Gender *
            </label>
            <select
              id="gender"
              value={formData.gender}
              onChange={(e) => handleInputChange('gender', e.target.value)}
              className={`block w-full px-3 py-2 border rounded-md shadow-sm focus:outline-none focus:ring-green-500 focus:border-green-500 sm:text-sm ${
                errors.gender ? 'border-red-300' : 'border-gray-300'
              }`}
            >
              <option value="male">Male</option>
              <option value="female">Female</option>
              <option value="other">Other</option>
            </select>
            {errors.gender && (
              <p className="mt-1 text-sm text-red-600">{errors.gender}</p>
            )}
          </div>

          <div>
            <label htmlFor="onboardingDate" className="block text-sm font-medium text-gray-700 mb-2">
              <Calendar className="inline h-4 w-4 mr-1" />
              Onboarding Date *
            </label>
            <input
              type="date"
              id="onboardingDate"
              value={formData.onboardingDate}
              onChange={(e) => handleInputChange('onboardingDate', e.target.value)}
              max={new Date().toISOString().split('T')[0]}
              className={`block w-full px-3 py-2 border rounded-md shadow-sm focus:outline-none focus:ring-green-500 focus:border-green-500 sm:text-sm ${
                errors.onboardingDate ? 'border-red-300' : 'border-gray-300'
              }`}
            />
            {errors.onboardingDate && (
              <p className="mt-1 text-sm text-red-600">{errors.onboardingDate}</p>
            )}
          </div>
        </div>

        {/* Availability Status and Salary */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div>
            <label htmlFor="availabilityStatus" className="block text-sm font-medium text-gray-700 mb-2">
              Availability Status *
            </label>
            <select
              id="availabilityStatus"
              value={formData.availabilityStatus}
              onChange={(e) => handleInputChange('availabilityStatus', e.target.value)}
              className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-green-500 focus:border-green-500 sm:text-sm"
            >
              <option value="active">Active</option>
              <option value="inactive">Inactive</option>
              <option value="on_leave">On Leave</option>
            </select>
          </div>

          <div>
            <label htmlFor="salary" className="block text-sm font-medium text-gray-700 mb-2">
              <IndianRupee className="inline h-4 w-4 mr-1" />
              Weekly Salary (Optional)
            </label>
            <input
              type="number"
              id="salary"
              value={formData.salary || ''}
              onChange={(e) => handleInputChange('salary', e.target.value ? Number(e.target.value) : undefined)}
              min="0"
              step="10"
              className={`block w-full px-3 py-2 border rounded-md shadow-sm focus:outline-none focus:ring-green-500 focus:border-green-500 sm:text-sm ${
                errors.salary ? 'border-red-300' : 'border-gray-300'
              }`}
              placeholder="Enter weekly salary"
            />
            {errors.salary && (
              <p className="mt-1 text-sm text-red-600">{errors.salary}</p>
            )}
          </div>
        </div>

        {/* Form Actions */}
        <div className="flex justify-end space-x-3 pt-6 border-t border-gray-200">
          <button
            type="button"
            onClick={() => navigate('/workers')}
            className="px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500"
          >
            Cancel
          </button>
          <button
            type="submit"
            disabled={saving}
            className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-green-600 hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <Save className="h-4 w-4 mr-2" />
            {saving ? 'Saving...' : (isEditing ? 'Update Worker' : 'Save Worker')}
          </button>
        </div>
      </form>
    </div>
  );
};

export default WorkerForm; 